import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { UserSearchModal } from './UserSearchModal';
import { NoticesButton } from './NoticesButton';
import { ConnectionStatusIndicator } from './ConnectionStatusIndicator';
import { useAudioStreams } from '../contexts/AudioStreamsContext';
import { setShellOverride } from '../mobile/useShellMode';

export const Navigation: React.FC = () => {
  const { user, logout, isAuthenticated, isImpersonating } = useAuth();
  const { drawerOpen, toggleDrawer } = useAudioStreams();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isImpersonationModalOpen, setIsImpersonationModalOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // frontend_tester admins see the impersonation control, but it is temporarily
  // disabled: the old localStorage token-swap can't work under HttpOnly session
  // auth. Tracked for a session-based rebuild (Monday: "Rebuild admin
  // impersonation for session-cookie auth").
  const canImpersonate = hasPermission('frontend_tester') && !isImpersonating;

  // Gate the "Mobile layout" menu item so it isn't clutter on a real desktop:
  // a user pinned to desktop on a phone still matches width (`,` = or), and a
  // coarse-pointer tablet user can opt in. Deliberately not the shell's `and`
  // rule from useShellMode.
  const canUseMobileShell =
    typeof window !== 'undefined' &&
    window.matchMedia('(max-width: 768px), (pointer: coarse)').matches;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav className="bg-chrome border-b border-dark-border h-10 flex items-center px-6">
      <div className="flex items-center justify-between w-full">
        {/* Left: Logo/Brand */}
        <Link
          to="/"
          className="flex items-center transition-opacity hover:opacity-80"
        >
          {/* Badge lockup already contains the wordmark, so no adjacent text. */}
          <img
            src="/logo-badge.svg"
            alt="Fuzion Trading"
            className="h-7 w-auto object-contain"
          />
        </Link>

        {/* Right: User actions */}
        {isAuthenticated ? (
          <div className="flex items-center gap-3">
            {/* Connection status + audio moved up from the dashboard toolbar:
                they are app-wide, not dashboard-scoped. */}
            <ConnectionStatusIndicator />

            {/* GEX-Ray Monitor popout (admin only) */}
            {hasPermission('frontend_tester') && (
              <button
                onClick={() => {
                  window.open(
                    '/gexray-monitor',
                    'gexray-monitor',
                    'width=700,height=1000,menubar=no,toolbar=no,location=no,status=no'
                  );
                }}
                className="flex items-center gap-1.5 px-2 py-1 text-gray-400 hover:text-bull-blue transition-colors"
                title="Open GEX-Ray Monitor"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <span className="text-xs">GEX</span>
              </button>
            )}

            {/* Impersonation Button (admins only) — temporarily disabled during
                the session-auth migration; see canImpersonate comment above. */}
            {canImpersonate && (
              <button
                type="button"
                disabled
                className="flex items-center gap-1.5 px-2 py-1 text-gray-600 cursor-not-allowed"
                title="Impersonation is temporarily disabled during the session-auth migration"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              </button>
            )}

            {/* Audio Streams drawer toggle */}
            <button
              data-tour="audio-streams"
              onClick={toggleDrawer}
              className={`flex items-center gap-1.5 px-2.5 py-1 text-xs border rounded transition-colors ${
                drawerOpen
                  ? 'text-accent-yellow bg-accent-yellow/10 border-accent-yellow/30'
                  : 'text-gray-300 bg-dark-bg hover:bg-dark-surface-light border-dark-border'
              }`}
              title={drawerOpen ? 'Close Audio Streams' : 'Open Audio Streams'}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5 9v6h4l5 4V5L9 9H5z" />
              </svg>
              Audio
            </button>

            {/* Notices Bell */}
            <NoticesButton />

            {/* Profile Dropdown */}
            <div className="relative" ref={dropdownRef} data-tour="profile-link">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-2 px-2 py-1 text-gray-300 hover:text-gray-100 transition-colors"
              >
                {/* Profile Icon */}
                <div className="w-6 h-6 rounded-full bg-dark-surface-light border border-dark-border flex items-center justify-center">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                {/* Dropdown arrow */}
                <svg
                  className={`w-3 h-3 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-dark-surface border border-dark-border rounded-lg shadow-xl py-1 z-50">
                  {/* User info */}
                  <div className="px-4 py-2 border-b border-dark-border">
                    <p className="text-sm font-medium text-gray-100">{user?.username}</p>
                    <p className="text-xs text-gray-400 truncate">{user?.email}</p>
                  </div>

                  {/* Account */}
                  <Link
                    to="/profile"
                    onClick={() => setIsDropdownOpen(false)}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:bg-dark-surface-light hover:text-gray-100 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Account
                  </Link>

                  {/* Billing */}
                  <Link
                    to="/profile?tab=billing"
                    onClick={() => setIsDropdownOpen(false)}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:bg-dark-surface-light hover:text-gray-100 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                    Billing
                  </Link>

                  {/* Discord */}
                  <Link
                    to="/profile?tab=discord"
                    onClick={() => setIsDropdownOpen(false)}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:bg-dark-surface-light hover:text-gray-100 transition-colors"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z" />
                    </svg>
                    Discord
                  </Link>

                  {/* My Courses */}
                  <Link
                    to="/courses"
                    onClick={() => setIsDropdownOpen(false)}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:bg-dark-surface-light hover:text-gray-100 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                    My Courses
                  </Link>

                  {/* Mobile layout — return path from the desktop shell back
                      to mobile; setShellOverride('desktop') is otherwise a
                      one-way door. */}
                  {canUseMobileShell && (
                    <button
                      onClick={() => {
                        setShellOverride('mobile');
                        setIsDropdownOpen(false);
                      }}
                      className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-300 hover:bg-dark-surface-light hover:text-gray-100 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      Mobile layout
                    </button>
                  )}

                  {/* Logout */}
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-300 hover:bg-dark-surface-light hover:text-bear-red transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <Link
            to="/login"
            className="px-4 py-1.5 text-sm font-medium bg-accent-yellow hover:bg-wheat text-hot-tag-text rounded transition-colors"
          >
            Login
          </Link>
        )}
      </div>

      {/* Impersonation Modal */}
      <UserSearchModal
        isOpen={isImpersonationModalOpen}
        onClose={() => setIsImpersonationModalOpen(false)}
      />
    </nav>
  );
};
